import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  PATIENT_REPORT_PLACEHOLDERS,
  type PlaceholderGroup,
} from '@/lib/patientReports/placeholders';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SectionCard } from '@/pages/settings/settingsChrome';

const GROUPS: readonly PlaceholderGroup[] = ['report', 'patient', 'content'];

export function PlaceholderCatalog() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLocaleLowerCase('da');

  const visible = useMemo(
    () =>
      PATIENT_REPORT_PLACEHOLDERS.filter((item) => {
        if (item.catalog === false) return false;
        if (!needle) return true;
        const description = t(item.descriptionKey).toLocaleLowerCase('da');
        return `${item.token} ${description}`.toLocaleLowerCase('da').includes(needle);
      }),
    [needle, t],
  );

  return (
    <SectionCard
      title={t('patientReports.placeholders_title')}
      subtitle={t('patientReports.placeholders_hint')}
    >
      <p className={cn(TYPE.small, 'mb-s4 text-ds-muted')}>
        {t('patientReports.placeholders_header_note')}
      </p>
      <div data-testid="placeholder-catalog" className="flex flex-col gap-s4">
        <Input
          id="patient-report-placeholder-search"
          label={t('patientReports.placeholders_search')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {visible.length === 0 ? (
          <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.placeholders_empty')}</p>
        ) : (
          GROUPS.map((group) => {
            const items = visible.filter((item) => item.group === group);
            if (items.length === 0) return null;
            return (
              <section key={group} className="flex flex-col gap-s2">
                <h3 className={cn(TYPE.body, 'font-medium text-ds-text')}>
                  {t(`patientReports.placeholders_group_${group}`)}
                </h3>
                <ul className="divide-y divide-ds-line rounded-ctl border border-ds-line">
                  {items.map((item) => (
                    <li key={item.token} className="flex flex-wrap items-center gap-s3 px-s4 py-s3">
                      <code className={cn(TYPE.body, 'font-medium text-ds-text')}>
                        {item.token}
                      </code>
                      <p className={cn(TYPE.small, 'min-w-[12rem] flex-1 text-ds-muted')}>
                        {t(item.descriptionKey)}
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        aria-label={`${t('patientReports.placeholders_copy')} ${item.token}`}
                        onClick={() => {
                          void navigator.clipboard
                            .writeText(item.token)
                            .then(() => toast.success(t('patientReports.placeholders_copied')))
                            .catch(() => toast.error(t('patientReports.placeholders_copy_error')));
                        }}
                      >
                        {t('patientReports.placeholders_copy')}
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })
        )}
      </div>
    </SectionCard>
  );
}
